import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { installFetchInterceptor } from "@/utils/fetchInterceptor";
import { installPerformanceFetchObserver } from "@/utils/performanceMonitor";

// Install the JWT-staleness fetch interceptor BEFORE React renders so
// every API call (including those fired during the first paint) is
// covered. Idempotent — safe even if a future caller re-imports it.
installFetchInterceptor();
installPerformanceFetchObserver();

// -----------------------------------------------------------------------------
// Global ChunkLoadError recovery
// -----------------------------------------------------------------------------
// When we ship a new build, the old `index.html` (cached in the browser / by
// Cloudflare for up to 5 min) keeps referencing chunk filenames that no longer
// exist on the CDN. React.lazy then throws a `ChunkLoadError`. Without this
// handler the user is stuck on a blank screen until they hard-refresh.
//
// What we do here is the minimal industry-standard safety net:
//   1. Listen for `error` (sync throws) and `unhandledrejection` (async
//      dynamic-import failures).
//   2. Match ONLY the three canonical chunk-failure signatures — never
//      generic runtime errors.
//   3. Trigger `window.location.reload()` AT MOST ONCE per browser session,
//      gated by a sessionStorage flag so we can never enter a reload loop.
//   4. Stay completely passive when nothing is wrong.
//
// The ErrorBoundary inside <App/> covers the same cases for errors that
// escape into React's render path. The two layers are intentional belt+
// suspenders: this listener catches global rejections that never reach the
// boundary; the boundary catches errors that never reach the listener.
// -----------------------------------------------------------------------------
const RELOAD_FLAG = 'fs_chunk_recovery_reloaded';

const looksLikeChunkError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = ((err.message || err.toString && err.toString()) || '').toLowerCase();
  return (
    (msg.includes('loading chunk') && msg.includes('failed')) ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
};

const attemptRecoveryReload = () => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, '1');
    console.warn('[chunk-recovery] reloading once to refresh stale HTML');
    window.location.reload();
    return true;
  } catch {
    // sessionStorage unavailable — refuse to reload so we don't loop.
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (looksLikeChunkError(event && event.error)) {
      attemptRecoveryReload();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (looksLikeChunkError(event && event.reason)) {
      attemptRecoveryReload();
    }
  });
}
// -----------------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
